import React, { useEffect, useRef, useState } from "react";
import {
  View,
  StyleSheet,
  Animated,
  Dimensions,
  Easing,
  Image,
} from "react-native";
import disgoChipIcon from "../../../assets/disgo-chip-icon.png";

const CHIP_SIZE = 56;

/**
 * Full-screen overlay of chips flying in from the edges. When active, spawns chips;
 * when inactive, stops spawning but lets in-flight chips finish their animation.
 * @param {boolean} active - When true, spawn new chips. When false, only existing chips keep animating.
 */
function HousePickLoadingChips({ active }) {
  const [flyingLoadingChips, setFlyingLoadingChips] = useState([]);
  const loadingChipIntervalRef = useRef(null);
  const loadingChipIdCounter = useRef(0);
  const chipsRef = useRef([]);

  useEffect(() => {
    if (!active) {
      if (loadingChipIntervalRef.current) {
        clearInterval(loadingChipIntervalRef.current);
        loadingChipIntervalRef.current = null;
      }
      return;
    }

    if (loadingChipIntervalRef.current) return;

    const screenWidth = Dimensions.get("window").width;
    const screenHeight = Dimensions.get("window").height;

    const spawnChip = () => {
      const edge = Math.floor(Math.random() * 4);
      let startX, startY, endX, endY;

      switch (edge) {
        case 0:
          startX = Math.random() * screenWidth;
          startY = -CHIP_SIZE;
          endX = Math.random() * screenWidth;
          endY = screenHeight + CHIP_SIZE;
          break;
        case 1:
          startX = screenWidth + CHIP_SIZE;
          startY = Math.random() * screenHeight;
          endX = -CHIP_SIZE;
          endY = Math.random() * screenHeight;
          break;
        case 2:
          startX = Math.random() * screenWidth;
          startY = screenHeight + CHIP_SIZE;
          endX = Math.random() * screenWidth;
          endY = -CHIP_SIZE;
          break;
        case 3:
          startX = -CHIP_SIZE;
          startY = Math.random() * screenHeight;
          endX = screenWidth + CHIP_SIZE;
          endY = Math.random() * screenHeight;
          break;
      }

      const chipId = loadingChipIdCounter.current++;
      const deltaX = endX - startX;
      const deltaY = endY - startY;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const duration = Math.max(1000, Math.min(2000, distance * 0.8));

      const progress = new Animated.Value(0);

      const chip = {
        id: chipId,
        startX,
        startY,
        deltaX,
        deltaY,
        progress,
        rotation: Math.random() * 360,
      };

      chipsRef.current = [...chipsRef.current, chip];
      requestAnimationFrame(() => setFlyingLoadingChips([...chipsRef.current]));

      Animated.timing(progress, {
        toValue: 1,
        duration,
        easing: Easing.linear,
        useNativeDriver: true,
      }).start(() => {
        chipsRef.current = chipsRef.current.filter((c) => c.id !== chipId);
        requestAnimationFrame(() => setFlyingLoadingChips([...chipsRef.current]));
      });
    };

    spawnChip();
    const t1 = setTimeout(() => spawnChip(), 30);
    const t2 = setTimeout(() => spawnChip(), 60);
    const t3 = setTimeout(() => spawnChip(), 90);
    const t4 = setTimeout(() => spawnChip(), 120);
    const t5 = setTimeout(() => spawnChip(), 150);
    loadingChipIntervalRef.current = setInterval(() => {
      spawnChip();
      spawnChip();
    }, 100);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      clearTimeout(t5);
      if (loadingChipIntervalRef.current) {
        clearInterval(loadingChipIntervalRef.current);
        loadingChipIntervalRef.current = null;
      }
    };
  }, [active]);

  if (flyingLoadingChips.length === 0) return null;

  return (
    <View pointerEvents="none" style={styles.loadingChipsOverlay}>
      {flyingLoadingChips.map((chip) => (
        <Animated.View
          key={chip.id}
          pointerEvents="none"
          style={[
            styles.loadingChipLayer,
            {
              left: chip.startX,
              top: chip.startY,
              width: CHIP_SIZE,
              height: CHIP_SIZE,
              transform: [
                {
                  translateX: chip.progress.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, chip.deltaX],
                  }),
                },
                {
                  translateY: chip.progress.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, chip.deltaY],
                  }),
                },
                {
                  rotate: chip.progress.interpolate({
                    inputRange: [0, 1],
                    outputRange: [`${chip.rotation}deg`, `${chip.rotation + 360}deg`],
                  }),
                },
              ],
              opacity: chip.progress.interpolate({
                inputRange: [0, 0.1, 0.9, 1],
                outputRange: [0, 1, 1, 0],
              }),
            },
          ]}
        >
          <Image
            source={disgoChipIcon}
            style={styles.loadingChipImage}
            resizeMode="contain"
          />
        </Animated.View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  loadingChipsOverlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1000,
  },
  loadingChipLayer: {
    position: "absolute",
    zIndex: 1000,
  },
  loadingChipImage: {
    width: "100%",
    height: "100%",
  },
});

export default HousePickLoadingChips;
